import {
  ArrowTrendingUpIcon,
  BriefcaseIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  PencilSquareIcon,
  UserGroupIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { lazy } from "react";

import type { FeatureMetadata } from "../feature.types";
import {
  buildCrudPermissions,
  permissionId,
  createLazyPlaceholderPage,
} from "../shared/featureFactories";

import { DepartmentsFeature } from "./departments/departments.feature";
import { EmployeesFeature } from "./employees/employees.feature";

const TeachersPage = lazy(() => import("./teachers/pages/Teachers"));
const StaffPage = lazy(() => import("./staff/pages/Staff"));
const StaffAttendancePage = lazy(
  () => import("./staffAttendance/pages/StaffAttendancePage"),
);

const configs = [
  {
    id: "leave-management",
    label: "Leave Management",
    path: "/leave-management",
    icon: PencilSquareIcon,
    order: 4,
    permissions: buildCrudPermissions("HumanResource", "LeaveManagement", {
      create: permissionId("HumanResource", "LeaveManagement", "apply"),
      edit: permissionId("HumanResource", "LeaveManagement", "approve"),
      delete: permissionId("HumanResource", "LeaveManagement", "approve"),
    }),
  },
  {
    id: "payroll",
    label: "Payroll",
    path: "/payroll",
    icon: BriefcaseIcon,
    order: 5,
    permissions: buildCrudPermissions("HumanResource", "Payroll", {
      create: permissionId("HumanResource", "Payroll", "generate"),
      edit: permissionId("HumanResource", "Payroll", "generate"),
      delete: permissionId("HumanResource", "Payroll", "generate"),
    }),
  },
  {
    id: "recruitment",
    label: "Recruitment",
    path: "/recruitment",
    icon: ClipboardDocumentListIcon,
    order: 6,
    permissions: buildCrudPermissions("HumanResource", "Recruitment"),
  },
  {
    id: "appraisals",
    label: "Appraisals",
    path: "/appraisals",
    icon: ArrowTrendingUpIcon,
    order: 7,
    permissions: buildCrudPermissions("HumanResource", "Appraisals"),
  },
] as const satisfies ReadonlyArray<{
  id: string;
  label: string;
  path: string;
  icon: FeatureMetadata["navigation"]["icon"];
  order: number;
  permissions: FeatureMetadata["permissions"];
}>;

const placeholderFeatures: FeatureMetadata[] = configs.map(
  ({ id, label, path, icon, order, permissions }) => ({
    id,
    moduleName: "humanResources",
    navigation: { label, path, icon, order },
    permissions,
    route: {
      path,
      element: createLazyPlaceholderPage(label),
    },
    readiness: "live",
  }),
);

export const TeachersFeature: FeatureMetadata = {
  id: "teachers",
  moduleName: "humanResources",
  navigation: {
    label: "Teachers",
    path: "/teachers",
    icon: UserGroupIcon,
    order: 1,
  },
  route: {
    path: "/teachers",
    element: TeachersPage,
  },
  permissions: buildCrudPermissions("HumanResource", "Teachers"),
  readiness: "live",
};

export const StaffFeature: FeatureMetadata = {
  id: "staff",
  moduleName: "humanResources",
  navigation: {
    label: "Staff",
    path: "/staff",
    icon: UsersIcon,
    order: 2,
  },
  route: {
    path: "/staff",
    element: StaffPage,
  },
  permissions: buildCrudPermissions("HumanResource", "NonTeachingStaff"),
  readiness: "live",
};

export const StaffAttendanceFeature: FeatureMetadata = {
  id: "staff-attendance",
  moduleName: "humanResources",
  navigation: {
    label: "Staff Attendance",
    path: "/staff-attendance",
    icon: ClockIcon,
    order: 3,
  },
  route: {
    path: "/staff-attendance",
    element: StaffAttendancePage,
  },
  permissions: buildCrudPermissions("HumanResource", "StaffAttendance", {
    create: permissionId("HumanResource", "StaffAttendance", "mark"),
    edit: permissionId("HumanResource", "StaffAttendance", "mark"),
    delete: permissionId("HumanResource", "StaffAttendance", "biometricSync"),
  }),
  readiness: "live",
};

export const HUMAN_RESOURCES_FEATURES = [
  DepartmentsFeature,
  EmployeesFeature,
  TeachersFeature,
  StaffFeature,
  StaffAttendanceFeature,
  ...placeholderFeatures,
] as const satisfies readonly FeatureMetadata[];
